import { useEffect, useState } from "react";
import client from "@/lib/api";
import { cn } from "@/lib/utils";
import { useNavigate } from "@/lib/useNav";
import { useChat } from "@/context/ChatContext";
import { Avatar } from "@/components/shared/UI";
import { Input } from "@/components/ui/input";
import { Search, Building2, User, Briefcase, Hotel } from "lucide-react";
import { useBlockData, BlockEmpty } from "@/components/dashboard/useBlockData";

function timeAgo(iso) {
  if (!iso) return "";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h`;
  return `${Math.floor(m / 1440)}d`;
}

export function ChatsBlock({ block }) {
  const s = block.settings || {};
  const { openWindow, tick } = useChat();
  const [rows] = useBlockData(() => client.get("/chats", { params: { filter: s.filter || "all" } }).then((r) => r.data), [s.filter, tick], 15000);
  if (!rows) return null;
  const list = rows.slice(0, s.limit || 8);
  if (!list.length) return <BlockEmpty>No conversations</BlockEmpty>;
  return (
    <div className="space-y-0.5">
      {list.map((c) => (
        <button key={c.id} onClick={() => openWindow(c.id, c.name)} data-testid={`block-${block.id}-chat-${c.id}`} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-slate-50">
          <Avatar name={c.name} className="h-8 w-8 shrink-0 text-xs" />
          <div className="min-w-0 flex-1">
            <p className={cn("truncate text-sm", c.unread ? "font-semibold text-slate-900" : "font-medium text-slate-700")}>{c.name}</p>
            <p className="truncate text-xs text-slate-400">{c.last_message_text || "No messages yet"}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <span className="text-[10px] text-slate-400">{timeAgo(c.last_message_at)}</span>
            {c.unread > 0 && <span className="rounded-full bg-sky-500 px-1.5 text-[10px] font-bold text-white">{c.unread}</span>}
          </div>
        </button>
      ))}
    </div>
  );
}

export function NotificationsBlock({ block }) {
  const s = block.settings || {};
  const navigate = useNavigate();
  const [data, , setData] = useBlockData(() => client.get("/notifications", { params: { limit: 50 } }).then((r) => r.data), [], 20000);
  if (!data) return null;
  const list = (data.items || []).filter((n) => !s.unreadOnly || !n.read).slice(0, s.limit || 10);
  const open = (n, e) => {
    if (!n.read) {
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === n.id ? { ...x, read: true } : x)) }));
      client.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    if (!n.link) return;
    if (e.ctrlKey || e.metaKey) window.open(n.link, "_blank", "noopener");
    else navigate(n.link);
  };
  if (!list.length) return <BlockEmpty>You're all caught up</BlockEmpty>;
  return (
    <div className="space-y-0.5">
      {list.map((n) => (
        <button key={n.id} onClick={(e) => open(n, e)} data-testid={`block-${block.id}-notif-${n.id}`} className="flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-slate-50">
          <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", n.read ? "bg-transparent" : "bg-sky-500")} />
          <div className="min-w-0 flex-1">
            <p className={cn("truncate text-sm", n.read ? "text-slate-600" : "font-semibold text-slate-900")}>{n.title}</p>
            {n.message && <p className="line-clamp-2 text-xs text-slate-400">{n.message}</p>}
          </div>
          <span className="shrink-0 text-[10px] text-slate-400">{timeAgo(n.created_at)}</span>
        </button>
      ))}
    </div>
  );
}

const GROUPS = {
  properties: { label: "Properties", icon: Hotel },
  contacts: { label: "Contacts", icon: User },
  organisations: { label: "Organisations", icon: Building2 },
  opportunities: { label: "Opportunities", icon: Briefcase },
};

function groupResults(data) {
  const orgs = data.organisations || [];
  return {
    properties: [
      ...orgs.filter((o) => o.org_type === "DTR Property").map((o) => ({ id: o.id, label: o.name, href: `/properties/${o.id}` })),
    ],
    organisations: orgs.filter((o) => o.org_type !== "DTR Property").map((o) => ({ id: o.id, label: o.name, sub: o.org_type, href: `/organisations/${o.id}` })),
    contacts: (data.contacts || []).map((c) => ({ id: c.id, label: [c.first_name, c.last_name].filter(Boolean).join(" "), sub: c.email, href: `/contacts/${c.id}` })),
    opportunities: (data.opportunities || []).map((o) => ({ id: o.id, label: o.title, href: `/opportunities/${o.id}` })),
  };
}

export function QuickSearchBlock({ block }) {
  const s = block.settings || {};
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const ents = s.entities?.length ? s.entities : Object.keys(GROUPS);
  useEffect(() => {
    if (!q.trim()) { setRes(null); return undefined; }
    const t = setTimeout(() => client.get("/search", { params: { q: q.trim() } }).then(({ data }) => setRes(groupResults(data))).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const any = res && ents.some((k) => res[k]?.length);
  return (
    <div className="relative" onFocus={() => setOpen(true)} onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) && setOpen(false)} onKeyDown={(e) => e.key === "Escape" && setOpen(false)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${ents.map((k) => GROUPS[k].label.toLowerCase()).join(", ")}…`} className="pl-9" data-testid={`block-${block.id}-search-input`} />
      </div>
      {res && open && (
      <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-96 space-y-3 overflow-auto rounded-xl border border-slate-200 bg-white p-3 shadow-xl" data-testid={`block-${block.id}-results`}>
        {!any && <BlockEmpty>No matches</BlockEmpty>}
        {res && ents.filter((k) => res[k]?.length).map((k) => {
          const G = GROUPS[k];
          return (
            <div key={k}>
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">{G.label}</p>
              {res[k].map((r) => (
                <button key={r.id} onClick={() => navigate(r.href)} data-testid={`block-${block.id}-result-${r.id}`} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50">
                  <G.icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="truncate text-sm text-slate-800">{r.label}</span>
                  {r.sub && <span className="truncate text-xs text-slate-400">{r.sub}</span>}
                </button>
              ))}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
